import React from 'react';
import SectionHeading from './SectionHeading';
import useScrollReveal from '../hooks/useScrollReveal';

const SCHOLAR =
  'https://scholar.google.com/citations?user=xY8c1x8AAAAJ&hl=en';

/**
 * Transcribed from the live Google Scholar profile rather than from a CV, so the
 * counts and citation numbers match what a reader finds if they follow the link.
 */

const GROUPS = [
  {
    label: 'Peer reviewed journal articles',
    items: [
      {
        title: 'Ensemble methods for parameter estimation of WRF-Hydro',
        venue: 'Water Resources Research 61 (1)',
        year: '2025',
        authors: 'A RafieeiNasab, MN Fienen, N Omani, I Srivastava, AL Dugger',
        cited: 14,
      },
      {
        title:
          'A WRF-Hydro-based retrospective simulation of water resources for US integrated water availability assessment',
        venue: 'Hydrology and Earth System Sciences',
        year: '2024',
        authors: 'A Rafieeinasab, A Mazrooei, T Enzminger, I Srivastava, A Dugger, et al.',
        cited: 6,
      },
    ],
  },
  {
    label: 'USGS data releases',
    items: [
      {
        title:
          'CONUS404 climate forcing variable subset for hydrologic models, 1979 to 2022, downscaled to 1 km and bias adjusted for precipitation and temperature',
        venue: 'US Geological Survey Data Release 640',
        year: '2024',
        authors: 'Y Zhang, JA Grim, RS Cabell, I Srivastava, DJ Gochis, AF Prein, et al.',
        cited: 2,
      },
      {
        title:
          'Application of the WRF-Hydro Modeling System for the Conterminous United States at NHDPlus version 2 spatial resolution using the bias adjusted CONUS404',
        venue: 'US Geological Survey Data Release 1111',
        year: '2024',
        authors: 'A RafieeiNasab, D Gochis, I Srivastava, A Dugger, K Sampson, N Omani, et al.',
        cited: 1,
      },
      {
        title:
          'Application of the WRF-Hydro Modeling System for the Conterminous United States using the bias adjusted CONUS404 atmospheric forcings (CONUS404BA)',
        venue: 'US Geological Survey Data Release 450',
        year: '2026',
        authors: 'A RafieeiNasab, Y Zhang, A Dugger, A Mazrooei, DJ Gochis, I Srivastava, et al.',
      },
    ],
  },
  {
    label: 'Technical documentation',
    items: [
      {
        title: 'The WRF-Hydro Modeling System Technical Description, Version 5.4',
        venue: 'NCAR Technical Note',
        year: '2025',
      },
    ],
  },
  {
    label: 'Conference presentations',
    items: [
      {
        title: 'METplus in Action: Bridging Research and Operations for Model Verification',
        venue: '106th AMS Annual Meeting',
        year: '2026',
        authors: 'MA Harrold, M Smith, DR Adriaansen, JH Gotway, TJ Hertneky, CP Kalb, et al.',
      },
      {
        title:
          'Challenges and Insights in Transitioning Hydrologic Data Assimilation from Regional to CONUS Scale',
        venue: 'AGU Fall Meeting',
        year: '2025',
        authors: 'A Rafieeinasab, M Gharamti, M Casali, I Srivastava, AL Dugger',
      },
      {
        title:
          'The Integration of Probabilistic Precipitation Forecasts from the National Blend of Models into NOAA’s National Water Model: A Case Study of Hurricane Beryl',
        venue: '105th AMS Annual Meeting, 39HYDRO',
        year: '2025',
        authors: 'A Mazrooei, I Srivastava, R Cabell, Y Zhang, A McKluskey, DJ Gochis',
      },
      {
        title:
          'Enhancing Streamflow Drought Simulation through Calibration and Surface Data Assimilation for the US Drought Monitor',
        venue: '105th AMS Annual Meeting',
        year: '2025',
        authors: 'TM Lahmers, A Dugger, SV Kumar, MD Svoboda, T Haigh, A Mazrooei, et al.',
      },
      {
        title:
          'Enhancing streamflow drought simulation through calibration and surface data assimilation for the US Drought Monitor',
        venue: 'Water Science Conference, WaterSciCon24',
        year: '2024',
        authors: 'TM Lahmers, AL Dugger, SV Kumar, MD Svoboda, T Haigh, AH Mazrooei, et al.',
      },
      {
        title:
          'Ingesting the operational National Blend of Models meteorological forecasts into the operational NOAA National Water Model',
        venue: 'AGU Fall Meeting',
        year: '2023',
        authors: 'AH Mazrooei, Y Zhang, I Srivastava, R Cabell, A McCluskey, D Gochis',
      },
      {
        title: 'Regional Calibration of a WRF-Hydro Model Using PEST++',
        venue: 'AGU Fall Meeting',
        year: '2023',
        authors: 'N Omani, A Rafieeinasab, MN Fienen, I Srivastava, AL Dugger',
      },
      {
        title:
          'Uncertainty Analysis and Parameter Estimation for Landscape Models using an Iterative Ensemble Smoother',
        venue: 'AGU Fall Meeting',
        year: '2022',
        authors: 'MN Fienen, A Rafieeinasab, AL Dugger, P Saylor, I Srivastava',
      },
      {
        title: 'Cloud-Based Online Training Platform for the WRF-Hydro Model',
        venue: '102nd AMS Annual Meeting',
        year: '2022',
        authors: 'I Srivastava, R Cabell, K FitzGerald, M McAllister',
        first: true,
      },
      {
        title: 'Representing Fire Impacts in the NOAA National Water Model',
        venue: 'AGU Fall Meeting',
        year: '2021',
        authors: 'AL Dugger, M Casali, P Saylor, I Srivastava',
      },
      {
        title: 'NCAR’s Runway Friction and Closure Prediction System (RFCPS)',
        venue: '99th AMS Annual Meeting',
        year: '2019',
        authors: 'S Linden, G Wiener, T Brummet, W Petzke, I Srivastava',
        cited: 1,
      },
      {
        title:
          'Application of Machine Learning in NCAR’s Runway Friction and Closure Prediction System (RFCPS)',
        venue: '99th AMS Annual Meeting',
        year: '2019',
        authors: 'I Srivastava, S Linden, T Brummet, G Wiener, W Petzke',
        first: true,
      },
    ],
  },
];

const STATS = [
  { v: '18', l: 'Publications' },
  { v: '24', l: 'Citations' },
  { v: '2', l: 'h-index' },
  { v: '2', l: 'First author' },
];

const Group = ({ group }) => {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.05 });

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-calm ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <p className="eyebrow text-amber-bright">{group.label}</p>

      <ol className="mt-6">
        {group.items.map((item) => (
          <li key={item.title} className="border-t border-cream/15 py-6">
            <div className="flex flex-col gap-1.5 md:flex-row md:items-baseline md:gap-6">
              <p className="flex-1 text-[1.0625rem] leading-snug text-cream">
                {item.title}
                {item.first && (
                  <span className="ml-2 align-middle font-mono text-[0.6rem] uppercase tracking-eyebrow text-amber-bright">
                    First author
                  </span>
                )}
              </p>
              {item.year && (
                <span className="metric flex-none text-sm text-cream/45">{item.year}</span>
              )}
            </div>

            <p className="mt-2 font-mono text-[0.7rem] uppercase tracking-eyebrow text-cream/45">
              {item.venue}
              {item.cited != null && (
                <span className="text-amber-bright/80"> · cited {item.cited}</span>
              )}
            </p>

            {item.authors && (
              <p className="mt-2.5 text-sm text-cream/55">{item.authors}</p>
            )}

            {item.note && (
              <p className="mt-3 max-w-measure text-sm leading-relaxed text-cream/70">
                {item.note}
              </p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

const Publications = () => (
  <section id="publications" className="section bg-night text-cream">
    <div className="shell">
      <SectionHeading
        dark
        number="04"
        label="Publications"
        title="Eighteen publications in operational forecasting and hydrology."
        intro="Including Water Resources Research and Hydrology and Earth System Sciences. Two as first author."
      />

      <dl className="mt-14 grid grid-cols-2 gap-6 md:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.l} className="border border-cream/15 px-5 py-6">
            <dt className="sr-only">{s.l}</dt>
            <dd>
              <span className="figure text-3xl text-cream">{s.v}</span>
              <span className="mt-2 block font-mono text-[0.66rem] uppercase tracking-eyebrow text-cream/45">
                {s.l}
              </span>
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-16 space-y-14">
        {GROUPS.map((g) => (
          <Group key={g.label} group={g} />
        ))}
      </div>

      <a
        href={SCHOLAR}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-ghost-cream mt-14"
      >
        Google Scholar
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M7 17L17 7M17 7H8M17 7v9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    </div>
  </section>
);

export default Publications;
